import { httpClient } from '@/services/http/client'
import { buildApiUrl, ApiService } from '@/config/api.config'
import type { Article } from '@/types/entities/article'
import { ArticleType } from '@/types/enums/article-type'

/**
 * Параметры пагинации
 */
export interface PaginationParams {
  page?: number
  perPage?: number
}

/**
 * Данные пагинации из ответа сервера (proto JSON spec после DEV-182).
 */
export interface PaginationResponse {
  page: number
  perPage: number
  total: number
}

/**
 * Ответ с пагинацией
 */
export interface PaginatedResponse<T> {
  articles: T[]
  pagination: PaginationResponse
}

/**
 * API методы для Articles feature
 */
export const articlesApi = {
  /**
   * Получить статью по slug
   */
  async getBySlug(slug: string): Promise<Article> {
    const url = buildApiUrl(ApiService.Articles, `news/${slug}`)
    const response = await httpClient.get<{ article: Article }>(url)
    return response.article
  },

  /**
   * Получить список статей с фильтрами и пагинацией
   */
  async list(
    type?: ArticleType,
    pagination?: PaginationParams,
    tags?: string[]
  ): Promise<PaginatedResponse<Article>> {
    const url = buildApiUrl(ApiService.Articles, 'articles/list')
    const params = new URLSearchParams()

    // Фильтр по типу
    if (type !== undefined) {
      params.append('filter.type', type.toString())
    }

    // Пагинация
    if (pagination?.page !== undefined) {
      params.append('pagination.page', pagination.page.toString())
    }
    if (pagination?.perPage !== undefined) {
      params.append('pagination.per_page', pagination.perPage.toString())
    }

    // Теги: имя query-параметра должно совпадать с proto-полем ArticlesFilter.tag_names
    // (gRPC-gateway матчит snake_case/camelCase, но не 'tags'). DEV-217.
    if (tags && tags.length > 0) {
      tags.forEach(tag => params.append('filter.tag_names', tag))
    }

    const fullUrl = `${url}?${params}`
    const response = await httpClient.get<PaginatedResponse<Article>>(fullUrl)

    // proto3 JSON сериализует uint64 (page/per_page/total) строками — приводим
    // к числам, иначе арифметика пагинации в сторе ломается ("1" + 1 = "11").
    const p = response.pagination
    return {
      articles: response.articles || [],
      pagination: {
        page: Number(p?.page) || 1,
        perPage: Number(p?.perPage) || (pagination?.perPage ?? 12),
        total: Number(p?.total) || 0,
      },
    }
  },

  /**
   * Получить featured новости для главной страницы
   * Использует list() с типом NEWS и пагинацией
   */
  async getFeaturedNews(): Promise<Article[]> {
    const response = await this.list(
      ArticleType.NEWS,
      { page: 1, perPage: 4 }
    )
    return response.articles
  },

  /**
   * Получить избранные статьи
   */
  async getFeatured(): Promise<Article[]> {
    const url = buildApiUrl(ApiService.Articles, 'articles/featured')
    const response = await httpClient.get<PaginatedResponse<Article>>(url)
    return response.articles || []
  },

  /**
   * DEV-168: количество опубликованных статей по каждому ArticleType.
   * Используется в меню «Статьи» — подразделы со счётчиком 0 скрываются.
   * Сервер возвращает только типы с count > 0; остальные считаем равными 0.
   */
  async getSectionCounts(): Promise<Record<string, number>> {
    const url = buildApiUrl(ApiService.Articles, 'articles/section-counts')
    const response = await httpClient.get<{
      counts?: { type: string; count: number }[]
    }>(url)
    const result: Record<string, number> = {}
    for (const entry of response.counts ?? []) {
      result[entry.type] = entry.count
    }
    return result
  },
}
